import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Checkbox, Button, Spin, message } from 'antd';
import { TagFilled, CloseOutlined } from '@ant-design/icons';
import './AddLabelModal.less';

// Minimal label shape the modal needs — both WorkOrderLabel and the parts Label
// satisfy it. Kept independent of any one entity so the modal stays reusable.
export interface AddLabelModalLabel {
	id: number;
	label: string;
	color?: string;
}

type SelectValue = number | string;

// Entity-agnostic Add Label modal. All entity-specific behavior (data fetching,
// assignment side effects, the edit drawer, the success/error toasts) is injected
// via props so both work orders and parts can share this UI. The wrappers below
// supply those bindings.
interface AddLabelModalProps {
	visible: boolean;
	onClose: () => void;
	entityId: number | string;
	existingLabels?: AddLabelModalLabel[];
	canModifyLabels: boolean;
	canApplyLabels: boolean;
	catalogue: AddLabelModalLabel[];
	catalogueFetching: boolean;
	fetchLabels: (search: string) => void;
	createLabel: (entity: { label: string; color: string }) => Promise<AddLabelModalLabel>;
	assignLabels: (entityId: number | string, ids: number[]) => Promise<any>;
	// Receives the colors already in use so the picker can avoid handing out a duplicate.
	pickNewLabelColor: (usedColors?: string[]) => string;
	// Minimum trimmed length before the inline "+ Create New Label" row appears.
	minCreateLength?: number;
	// Assignment side-effect hooks — wrappers wire entity-specific behavior here.
	onBeforeAssign?: (entityId: number | string) => void;
	onAfterAssign?: (
		entityId: number | string,
		savedLabels: AddLabelModalLabel[],
		info: { wasEmpty: boolean }
	) => void;
	onAssignError?: (entityId: number | string) => void;
	onAssignSettled?: (entityId: number | string) => void;
}

const LabelGlyph: React.FC<{ color?: string }> = ({ color }) => (
	<TagFilled className="text-sm" style={{ color }} />
);

export const AddLabelModal: React.FC<AddLabelModalProps> = ({
	visible,
	onClose,
	entityId,
	existingLabels = [],
	canModifyLabels,
	canApplyLabels,
	catalogue,
	catalogueFetching,
	fetchLabels,
	createLabel,
	assignLabels,
	pickNewLabelColor,
	minCreateLength = 1,
	onBeforeAssign,
	onAfterAssign,
	onAssignError,
	onAssignSettled,
}) => {
	const existingIds = existingLabels.map((label) => label.id);
	const [selectedIds, setSelectedIds] = useState<SelectValue[]>(existingIds);
	const [searchText, setSearchText] = useState('');
	const [draftCreateLabelColor, setDraftCreateLabelColor] = useState<string | null>(null);
	// Labels created inline this session that may not be in the store catalogue yet.
	const [locallyCreated, setLocallyCreated] = useState<AddLabelModalLabel[]>([]);
	const [isCreatingLabel, setIsCreatingLabel] = useState(false);
	const [isAssigningLabels, setIsAssigningLabels] = useState(false);

	// Load the catalogue and reset per-entity state only when the modal opens. `existingLabels`
	// and `catalogue` are fresh array identities every render, so they must NOT be deps —
	// depending on them re-runs the fetch each render and spins an infinite request loop.
	useEffect(() => {
		if (!visible) return;
		setSelectedIds(existingLabels.map((label) => label.id));
		setSearchText('');
		setDraftCreateLabelColor(null);
		setLocallyCreated([]);
		setIsCreatingLabel(false);
		setIsAssigningLabels(false);
		fetchLabels('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, entityId]);

	// Working set = the loaded catalogue plus any just-created labels not yet reflected in it.
	// Derived (not state) so it can't feed an update loop as `catalogue` identity changes.
	const labels = useMemo(() => {
		const byId = new Map<SelectValue, AddLabelModalLabel>();
		catalogue.forEach((label) => byId.set(label.id, label));
		locallyCreated.forEach((label) => {
			if (!byId.has(label.id)) byId.set(label.id, label);
		});
		return Array.from(byId.values());
	}, [catalogue, locallyCreated]);

	// Resolves an id to a label for selected chips and save. Seeded with the entity's
	// already-assigned labels so chips render even if one is missing from the catalogue.
	const labelById = useMemo(() => {
		const map = new Map<SelectValue, AddLabelModalLabel>();
		existingLabels.forEach((label) => map.set(label.id, label));
		labels.forEach((label) => map.set(label.id, label));
		return map;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [labels, existingLabels]);

	const baseOptions = useMemo(
		() =>
			labels.map((label) => ({
				value: label.id as SelectValue,
				label: label.label,
				color: label.color,
			})),
		[labels]
	);

	const trimmedSearch = searchText.trim();
	const matchExists =
		!!trimmedSearch &&
		labels.some((label) => label.label.toLowerCase() === trimmedSearch.toLowerCase());

	const showCreateNewLabelRow =
		trimmedSearch.length >= minCreateLength && !matchExists && canModifyLabels;

	// Colors a new label must avoid: everything the catalogue has shown this session plus the
	// entity's own chips and anything created inline (which a narrowed search can drop).
	const usedColors = useMemo(
		() =>
			Array.from(labelById.values())
				.map((label) => label.color)
				.filter(Boolean),
		[labelById]
	);

	useEffect(() => {
		if (showCreateNewLabelRow) {
			// Pick outside the updater: it can run twice under StrictMode, and the pick is
			// random, so the color React keeps would not be the one computed last.
			const nextColor = pickNewLabelColor(usedColors);
			setDraftCreateLabelColor((current) => current || nextColor);
			return;
		}
		setDraftCreateLabelColor(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showCreateNewLabelRow]);

	const clearSearchInput = () => setSearchText('');

	const handleDropdownVisibleChange = (open: boolean) => {
		if (!open) {
			clearSearchInput();
		}
	};

	const handleCreateLabel = () => {
		if (!showCreateNewLabelRow || isCreatingLabel) return;
		const color = draftCreateLabelColor || pickNewLabelColor(usedColors);
		const optimisticName = trimmedSearch;
		clearSearchInput();
		setIsCreatingLabel(true);
		createLabel({ label: optimisticName, color })
			.then((created) => {
				if (!created) return;
				setLocallyCreated((current) =>
					current.some((label) => label.id === created.id) ? current : [...current, created]
				);
				setSelectedIds((current) =>
					current.includes(created.id) ? current : [...current, created.id]
				);
			})
			.catch(() => {
				message.error(`Failed to create label "${optimisticName}".`);
				setSearchText(optimisticName);
				setDraftCreateLabelColor(color);
			})
			.finally(() => {
				setIsCreatingLabel(false);
			});
	};

	const renderOption = (option: { value: SelectValue; label: string; color: string }) => (
		<div className="inline-flex w-full items-center gap-2 py-0.5">
			<Checkbox checked={selectedIds.includes(option.value)} />
			<LabelGlyph color={option.color} />
			<span>{option.label}</span>
		</div>
	);

	const tagRender = ({ value, onClose: onTagClose }: any) => {
		const label = labelById.get(value);
		if (!label) return <span />;
		return (
			<span className="mr-1 inline-flex h-[22px] items-center gap-1 rounded-[2px] bg-grey-3 px-2">
				<LabelGlyph color={label.color} />
				<span className="footnoteregular text-grey-10">{label.label}</span>
				{canApplyLabels ? (
					<CloseOutlined
						onClick={(event) => {
							event.stopPropagation();
							onTagClose();
						}}
						className="cursor-pointer text-[10px] text-grey-7"
					/>
				) : null}
			</span>
		);
	};

	const hasFilteredMatches = trimmedSearch
		? labels.some((label) => label.label.toLowerCase().includes(trimmedSearch.toLowerCase()))
		: labels.length > 0;

	const dropdownRender = (menu: React.ReactNode) => (
		<>
			{hasFilteredMatches ? menu : null}
			{showCreateNewLabelRow && draftCreateLabelColor ? (
				<div
					onMouseDown={(event) => event.preventDefault()}
					onClick={handleCreateLabel}
					className={`bodyregular flex cursor-pointer items-center gap-1.5 px-3 py-2 text-character-primary${
						hasFilteredMatches ? ' border-t border-solid border-grey-4' : ''
					}`}
				>
					<span>+ Create New Label:</span>
					<span className="text-character-secondary">“</span>
					<LabelGlyph color={draftCreateLabelColor} />
					<span>{trimmedSearch}</span>
					<span className="text-character-secondary">”</span>
				</div>
			) : null}
		</>
	);

	const handleSave = () => {
		if (isAssigningLabels || isCreatingLabel) return;
		const wasEmpty = existingIds.length === 0;
		const savedLabels = (selectedIds as SelectValue[])
			.map((labelId) => labelById.get(labelId))
			.filter((label): label is AddLabelModalLabel => !!label);
		const numericIds = savedLabels.map((label) => label.id);
		setIsAssigningLabels(true);
		if (onBeforeAssign) onBeforeAssign(entityId);
		assignLabels(entityId, numericIds)
			.then(() => {
				if (onAfterAssign) onAfterAssign(entityId, savedLabels, { wasEmpty });
				onClose();
			})
			.catch(() => {
				if (onAssignError) onAssignError(entityId);
			})
			.finally(() => {
				setIsAssigningLabels(false);
				if (onAssignSettled) onAssignSettled(entityId);
			});
	};

	// Cancel/X with unsaved assignment changes prompts a discard confirm; a clean modal closes
	// immediately. Outside-click and ESC never reach here (maskClosable/keyboard are off), so they
	// can't silently discard — they only dismiss the open Select dropdown.
	const attemptClose = () => {
		if (isAssigningLabels || isCreatingLabel) return;
		const selectedSet = new Set((selectedIds as SelectValue[]).map(Number));
		const dirty =
			selectedSet.size !== existingIds.length || existingIds.some((id) => !selectedSet.has(id));
		if (!dirty) {
			onClose();
			return;
		}
		Modal.confirm({
			title: 'Are you sure?',
			content: 'Changes will be discarded.',
			okText: 'Discard',
			cancelText: 'Cancel',
			okButtonProps: { danger: true },
			onOk: onClose,
		});
	};

	return (
		<>
			<Modal
				title={canApplyLabels ? 'Add Label' : 'Labels'}
				visible={visible}
				onCancel={isAssigningLabels ? undefined : attemptClose}
				closable={!isAssigningLabels}
				maskClosable={false}
				keyboard={false}
				width={572}
				destroyOnClose
				footer={
					<div className="flex items-center justify-end">
						<div className="flex gap-2">
							<Button
								onClick={attemptClose}
								disabled={isAssigningLabels || isCreatingLabel}
								className="!rounded-[2px]"
							>
								{canApplyLabels ? 'CANCEL' : 'CLOSE'}
							</Button>
							{canApplyLabels ? (
								<Button
									type="primary"
									onClick={handleSave}
									disabled={isCreatingLabel || isAssigningLabels}
									loading={isCreatingLabel || isAssigningLabels}
									className="!rounded-[2px]"
								>
									SAVE
								</Button>
							) : null}
						</div>
					</div>
				}
			>
				<div
					className={`add-label-select-wrapper relative${
						selectedIds.length > 0 ? ' has-selection' : ''
					}${selectedIds.length > 0 && !searchText ? ' show-placeholder-on-search-row' : ''}`}
				>
					<Select
						className="add-label-select w-full"
						mode="multiple"
						disabled={!canApplyLabels}
						suffixIcon={null}
						dropdownMatchSelectWidth
						listHeight={256}
						value={selectedIds as any}
						onChange={(value) => {
							setSelectedIds(value as SelectValue[]);
							clearSearchInput();
						}}
						onSearch={setSearchText}
						searchValue={searchText}
						onDropdownVisibleChange={handleDropdownVisibleChange}
						placeholder="search or begin typing to create new label"
						optionLabelProp="label"
						menuItemSelectedIcon={null}
						tagRender={tagRender}
						dropdownRender={dropdownRender}
						notFoundContent={
							catalogueFetching ? (
								<div className="flex justify-center py-2">
									<Spin size="small" />
								</div>
							) : (
								<span className="hidden" />
							)
						}
						filterOption={(input, option) =>
							String((option as any).label || '')
								.toLowerCase()
								.includes(input.trim().toLowerCase())
						}
					>
						{baseOptions.map((option) => (
							<Select.Option key={option.value} value={option.value} label={option.label}>
								{renderOption(option)}
							</Select.Option>
						))}
					</Select>
					{selectedIds.length > 0 && !searchText && canApplyLabels ? (
						<span className="add-label-select-placeholder bodyregular pointer-events-none absolute bottom-[7px] left-3 right-3 overflow-hidden text-ellipsis whitespace-nowrap text-character-disabled">
							search or begin typing to create new label
						</span>
					) : null}
				</div>
			</Modal>
		</>
	);
};
